/**
 * Where a chosen photograph is cut, and how big the square comes out.
 *
 * The backend takes a square avatar at most 2 MiB (media-handling ADR,
 * `train-with-me-app/docs/adr/2026-07-03-media-handling-architecture.md`), and a modern phone
 * camera produces a 12 megapixel rectangle. Everything between those two facts is arithmetic, and
 * it lives here rather than beside the canvas so a test can reach it without a DOM.
 *
 * Two rules, and the second one is the easy mistake:
 *
 * · The crop is the LARGEST centred square the photograph contains. Cutting from a corner would
 *   decapitate every portrait taken in landscape.
 * · A photograph SMALLER than the target is never scaled up. Upscaling costs bytes and adds no
 *   detail, and an avatar drawn from a 96px original looks worse enlarged to 512 than it does at
 *   its own size.
 */

/**
 * The longest edge an avatar is stored at. 512 is four times the largest box this app or the mobile
 * app draws one in (128px on the mobile profile hub), which covers a 3x display with room to spare.
 */
export const AVATAR_MAX_EDGE = 512;

/**
 * The most pixels this app will draw, mirroring `ImageSanitizer.MaxDecodePixels` on the backend.
 *
 * The byte ceiling alone does not bound the work: a 20 000 × 20 000 PNG of one flat colour is a few
 * hundred kilobytes on disk and 1.6 GB decoded, so the tab dies inside `drawImage` before any
 * refusal can be worded. Checked against the NATURAL size, after the decode and before the canvas.
 */
export const AVATAR_MAX_DECODE_PIXELS = 30_000_000;

/** Whether a decoded photograph is too big to draw. See `AVATAR_MAX_DECODE_PIXELS`. */
export function exceedsAvatarDecodeCeiling(width: number, height: number): boolean {
  return width * height > AVATAR_MAX_DECODE_PIXELS;
}

export interface AvatarCrop {
  /** Where the square starts in the SOURCE image. */
  readonly sourceX: number;
  readonly sourceY: number;
  /** The square's edge in the source image. */
  readonly sourceEdge: number;
  /** The square's edge in the OUTPUT, which is the canvas's width and height. */
  readonly outputEdge: number;
}

/** @param width the source photograph's natural width @param height its natural height */
export function avatarCrop(width: number, height: number): AvatarCrop {
  const sourceEdge = Math.min(width, height);
  return {
    // `/ 2` on the remainder, so the square sits in the middle of the longer axis. Rounded, because
    // a fractional source rectangle makes `drawImage` resample a picture that needed no resampling.
    sourceX: Math.round((width - sourceEdge) / 2),
    sourceY: Math.round((height - sourceEdge) / 2),
    sourceEdge,
    outputEdge: Math.min(sourceEdge, AVATAR_MAX_EDGE),
  };
}
